import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutGrid } from "lucide-react";
import { PanelSelect } from "../../PanelSelect";
import { roomCircuitName } from "../roomCircuitName";
import { roomCircuitColorOf } from "./constants";
import type { RoomCircuitsBuilderProps, RoomCircuitsBuilderState } from "./types";

// Long enough to see the shape of it; the rest is a count.
const SHOWN_GROUPS = 8;

const rgbCss = ([r, g, b]: [number, number, number]) => `rgb(${r} ${g} ${b})`;

export const RoomCircuitsBuilder = ({
  boards,
  categoryName,
  defaultSize,
  plan: planFor,
  circuitName,
  onPreview,
  onTake,
}: RoomCircuitsBuilderProps) => {
  const { t } = useTranslation();
  const [state, setState] = useState<RoomCircuitsBuilderState>({ phase: "closed" });

  const size = state.phase === "open" ? Math.floor(Number(state.size)) : 0;
  const plan = useMemo(
    () =>
      state.phase === "open" && size >= 1
        ? planFor(state.boardLabel, size, state.includePlanCircuits)
        : null,
    [state, size, planFor],
  );

  useEffect(() => {
    onPreview(plan);
  }, [plan, onPreview]);
  useEffect(() => () => onPreview(null), [onPreview]);

  if (boards.length === 0) return null;

  if (state.phase === "closed") {
    return (
      <button
        type="button"
        data-testid="room-circuits-open"
        onClick={() =>
          setState({ phase: "open", boardLabel: boards[0].label, size: String(defaultSize), includePlanCircuits: false })
        }
        className="flex min-h-[40px] w-full items-center gap-2 rounded-lg border border-blue-300 bg-white px-3 text-left text-[14px] font-medium text-blue-700 hover:bg-blue-50"
      >
        <LayoutGrid size={16} className="flex-none" />
        <span className="min-w-0 flex-1">
          {t("smartSurveyor.cablesBeta.roomCircuitsOpen", { category: categoryName })}
        </span>
      </button>
    );
  }

  const groups = plan?.groups ?? [];
  const hidden = groups.length - SHOWN_GROUPS;
  const board = boards.find((one) => one.label === state.boardLabel) ?? boards[0];

  return (
    <div data-testid="room-circuits" className="flex flex-col gap-2 rounded-lg border border-blue-300 bg-blue-50/50 p-2.5">
      <div className="text-[14px] font-semibold text-slate-900">
        {t("smartSurveyor.cablesBeta.roomCircuitsTitle", { category: categoryName, board: board.name })}
      </div>
      <p className="text-[13px] leading-snug text-slate-600">
        {t(
          plan?.method === "rows"
            ? "smartSurveyor.cablesBeta.circuitAutoSplitByRows"
            : "smartSurveyor.cablesBeta.circuitAutoSplitByRooms",
        )}
      </p>

      <div className="flex flex-wrap items-center gap-1.5 text-[13px] text-slate-700">
        {boards.length > 1 && (
          <div className="min-w-[8rem] flex-1">
            <PanelSelect
              value={state.boardLabel}
              options={boards.map((one) => ({ value: one.label, label: one.name }))}
              placeholder={t("smartSurveyor.cablesBeta.roomCircuitsBoard")}
              ariaLabel={t("smartSurveyor.cablesBeta.roomCircuitsBoard")}
              onPick={(boardLabel) => setState({ ...state, boardLabel })}
            />
          </div>
        )}
        <span>{t("smartSurveyor.cablesBeta.circuitAutoSplitAtMost")}</span>
        <input
          type="number"
          min={1}
          value={state.size}
          aria-label={t("smartSurveyor.cablesBeta.circuitAutoSplitAtMost")}
          onChange={(event) => setState({ ...state, size: event.target.value })}
          className="h-8 w-16 rounded border border-slate-300 px-1.5 text-right tabular-nums"
        />
        <span>{t("smartSurveyor.cablesBeta.circuitAutoSplitPerCircuit")}</span>
      </div>

      {(plan?.keptCount ?? 0) > 0 || state.includePlanCircuits ? (
        <label className="flex items-start gap-2 text-[13px] text-slate-700">
          <input
            type="checkbox"
            checked={state.includePlanCircuits}
            onChange={(event) => setState({ ...state, includePlanCircuits: event.target.checked })}
            className="mt-0.5 h-4 w-4 flex-none"
          />
          <span>
            {state.includePlanCircuits
              ? t("smartSurveyor.cablesBeta.roomCircuitsIncludePlanOn")
              : t("smartSurveyor.cablesBeta.roomCircuitsIncludePlan", { count: plan?.keptCount ?? 0 })}
          </span>
        </label>
      ) : null}

      {groups.length === 0 ? (
        <p className="text-[13px] font-medium text-slate-700">{t("smartSurveyor.cablesBeta.roomCircuitsNothing")}</p>
      ) : (
        <ol className="flex flex-col gap-0.5">
          {groups.slice(0, SHOWN_GROUPS).map((group, index) => (
            <li
              key={group.deviceKeys[0]}
              data-testid="room-circuit-group"
              className="flex items-baseline gap-2 rounded bg-white/80 px-1.5 py-1 text-[13px]"
            >
              <span
                aria-hidden
                className="h-2.5 w-2.5 flex-none self-center rounded-sm"
                style={{ backgroundColor: rgbCss(roomCircuitColorOf(index)) }}
              />
              <span className="min-w-0 flex-1 truncate text-slate-800">
                {roomCircuitName({ roomNames: group.roomNames }) ||
                  t("smartSurveyor.cablesBeta.circuitAutoSplitRowGroup", { index: index + 1 })}
              </span>
              <span className="flex-none tabular-nums text-slate-600">
                {t("smartSurveyor.cablesBeta.circuitAutoSplitDevices", { count: group.deviceKeys.length })}
              </span>
              <span className="max-w-[6rem] flex-none truncate text-right text-[12px] text-slate-500">
                {group.circuitId
                  ? t("smartSurveyor.cablesBeta.roomCircuitsKeeps", { circuit: circuitName(group.circuitId) })
                  : t("smartSurveyor.cablesBeta.circuitAutoSplitNew")}
              </span>
            </li>
          ))}
        </ol>
      )}
      {hidden > 0 && (
        <p className="pl-5 text-[12.5px] text-slate-500">
          {t("smartSurveyor.cablesBeta.circuitAutoSplitMore", { count: hidden })}
        </p>
      )}
      {(plan?.emptiedCircuitIds.length ?? 0) > 0 && (
        <p className="text-[12.5px] text-amber-700">
          {t("smartSurveyor.cablesBeta.roomCircuitsEmptied", { count: plan!.emptiedCircuitIds.length })}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setState({ phase: "closed" })}
          className="rounded-md px-3 py-1.5 text-[13px] font-medium text-slate-600 hover:bg-white"
        >
          {t("smartSurveyor.cablesBeta.cancel")}
        </button>
        <button
          type="button"
          disabled={!plan || groups.length === 0}
          onClick={() => {
            if (!plan) return;
            onTake(state.boardLabel, plan);
            setState({ phase: "closed" });
          }}
          className="ml-auto rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-40"
        >
          {t("smartSurveyor.cablesBeta.roomCircuitsTake", { count: groups.length })}
        </button>
      </div>
    </div>
  );
};
